import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  Add as AddIcon,
  Functions as FunctionsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import sandboxesService from '../services/sandboxesService';
import projectsService from '../services/projectsService';
import api from '../services/authService';
import { LCM, CreateLCMForm, Project, Revision, Sandbox } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';

const LCMsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { projectId, revisionId, sandboxId } = useParams<{
    projectId: string;
    revisionId: string;
    sandboxId: string;
  }>();

  // State management
  const [lcms, setLcms] = useState<LCM[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [revision, setRevision] = useState<Revision | null>(null);
  const [sandbox, setSandbox] = useState<Sandbox | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fieldErrors = useFormFieldErrors();
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [createLCMOpen, setCreateLCMOpen] = useState(false);

  // Form states
  const [lcmForm, setLcmForm] = useState<CreateLCMForm>({
    prepro_name: '',
    prepro_data: {},
  });

  // Load initial data
  useEffect(() => {
    if (projectId && revisionId && sandboxId) {
      loadData();
    }
  }, [projectId, revisionId, sandboxId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [lcmsData, projectData, revisionData, sandboxData] = await Promise.all([
        sandboxesService.getLCMs(parseInt(projectId!), parseInt(revisionId!), parseInt(sandboxId!)),
        loadProject(),
        loadRevision(),
        loadSandbox(),
      ]);

      setLcms(lcmsData);
      setProject(projectData);
      setRevision(revisionData);
      setSandbox(sandboxData);
      setError(null);
    } catch (err: any) {
      setError('Failed to load LCMs');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProject = async (): Promise<Project | null> => {
    try {
      const projects = await projectsService.getProjects();
      return projects.find(p => p.id === parseInt(projectId!)) || null;
    } catch (err) {
      console.error('Error loading project:', err);
      return null;
    }
  };

  const loadRevision = async (): Promise<Revision | null> => {
    try {
      const revisions = await projectsService.getRevisions(parseInt(projectId!));
      return revisions.find(r => r.id === parseInt(revisionId!)) || null;
    } catch (err) {
      console.error('Error loading revision:', err);
      return null;
    }
  };

  const loadSandbox = async (): Promise<Sandbox | null> => {
    try {
      const sandboxes = await sandboxesService.getSandboxes(parseInt(projectId!), parseInt(revisionId!));
      return sandboxes.find(s => s.id === parseInt(sandboxId!)) || null;
    } catch (err) {
      console.error('Error loading sandbox:', err);
      return null;
    }
  };

  const handleCreateLCM = async () => {
    if (!lcmForm.prepro_name.trim()) {
      fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['prepro_name']);
      return;
    }
    try {
      await sandboxesService.createLCM(
        parseInt(projectId!),
        parseInt(revisionId!),
        parseInt(sandboxId!),
        lcmForm
      );
      setCreateLCMOpen(false);
      setLcmForm({
        prepro_name: '',
        prepro_data: {},
      });
      loadData(); // Refresh the list
    } catch (err: any) {
      fieldErrors.show(err.response?.data?.error || 'Failed to create LCM');
    }
  };

  const handleDeleteLCM = async (lcmId: number) => {
    if (!window.confirm('Are you sure you want to delete this LCM?')) return;

    try {
      await sandboxesService.deleteLCM(
        parseInt(projectId!),
        parseInt(revisionId!),
        parseInt(sandboxId!),
        lcmId
      );
      loadData(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete LCM');
    }
  };

  const handleOpenInLCMlab = (lcm: LCM) => {
    navigate(`/lcm-lab/${projectId}/${revisionId}/${sandboxId}/${lcm.id}`);
  };


  // Helper function to regenerate matrix from stored configuration
  const regenerateMatrix = (config: any): any[] => {
    const results: any[] = [];

    // Generate data dictionary from variables
    const data: Record<string, number[]> = {};
    Object.entries(config.variables).forEach(([name, params]: [string, any]) => {
      const values: number[] = [];
      for (let val = params.min; val <= params.max; val += params.step) {
        values.push(val);
      }
      data[name] = values;
    });

    // Get dependencies
    const dependencies = config.dependencies || [];

    // Get independent variables
    const allVars = Object.keys(data);
    const dependentVars = new Set(dependencies.flatMap((g: string[]) => g));
    const independentVars = allVars.filter(v => !dependentVars.has(v));

    // Generate combinations for dependency groups
    const groupCombinations: any[][] = [];
    dependencies.forEach((group: string[]) => {
      if (group.length > 0) {
        const groupData = group.map((v: string) => data[v]);
        const combinations = cartesianProduct(groupData);
        groupCombinations.push(combinations);
      }
    });

    // Generate combinations for independent variables
    let independentCombinations: any[][] = [[]];
    if (independentVars.length > 0) {
      const indData = independentVars.map(v => data[v]);
      independentCombinations = cartesianProduct(indData);
    }

    // Combine all combinations
    const allCombinations = cartesianProduct([
      ...groupCombinations.map(combo => [combo]),
      independentCombinations
    ]);

    // Build result objects
    allCombinations.forEach(combo => {
      const result: any = {};

      // Add group variables
      dependencies.forEach((group: string[], groupIndex: number) => {
        const groupCombo = combo[groupIndex];
        if (groupCombo && Array.isArray(groupCombo)) {
          group.forEach((varName: string, varIndex: number) => {
            result[varName] = groupCombo[varIndex];
          });
        }
      });

      // Add independent variables
      const indCombo = combo[combo.length - 1];
      if (indCombo && Array.isArray(indCombo)) {
        independentVars.forEach((varName: string, varIndex: number) => {
          result[varName] = indCombo[varIndex];
        });
      }

      results.push(result);
    });

    return results;
  };

  // Helper function for cartesian product
  const cartesianProduct = (arrays: any[][]): any[][] => {
    if (arrays.length === 0) return [[]];
    if (arrays.length === 1) return arrays[0].map(item => [item]);

    const [first, ...rest] = arrays;
    const restProduct = cartesianProduct(rest);

    const result: any[][] = [];
    first.forEach(item => {
      restProduct.forEach(combo => {
        result.push([item, ...combo]);
      });
    });

    return result;
  };

  const canManageLCMs = hasCapability(user, CAP.MANAGE_LCMS);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
        aria-label="breadcrumb"
      >
        <Link to="/projects" style={{ textDecoration: 'none', color: 'grey' }}>
          <Typography variant="body1" sx={{ color: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
            Projects
          </Typography>
        </Link>
        <Link to={`/projects/${projectId}/revisions`} style={{ textDecoration: 'none', color: 'grey' }}>
          <Typography variant="body1" sx={{ color: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
            {project?.name || `Project ${projectId}`}
          </Typography>
        </Link>
        <Link to={`/projects/${projectId}/revisions`} style={{ textDecoration: 'none', color: 'grey' }}>
          <Typography variant="body1" sx={{ color: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
            {revision?.revision_name || `Revision ${revisionId}`}
          </Typography>
        </Link>
        <Typography color="text.primary">{sandbox?.name || `Sandbox ${sandboxId}`}</Typography>
        <Typography color="text.primary">LCMs</Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Load Case Matrices
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {project?.name} - {revision?.revision_name} - {sandbox?.name}
          </Typography>
        </Box>

        {canManageLCMs && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateLCMOpen(true)}
            aria-label="Create new LCM"
          >
            New LCM
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} role="alert">
          {success}
        </Alert>
      )}


      <Grid container spacing={3}>
        {lcms.map((lcm) => (
          <Grid item xs={12} md={6} lg={4} key={lcm.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <FunctionsIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 500 }}>
                    {lcm.prepro_name}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`ID: ${lcm.id}`}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                </Box>

                {lcm.model_name && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Model: {lcm.model_name}
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Created: {lcm.created_at ? new Date(lcm.created_at).toLocaleDateString() : 'Unknown'}
                </Typography>

                {lcm.prepro_data && (
                  <Typography variant="body2" color="text.secondary">
                    Load Cases: {lcm.prepro_data.total_load_cases || 'N/A'}
                  </Typography>
                )}
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenInLCMlab(lcm);
                    }}
                    aria-label={`Open ${lcm.prepro_name} in LCM Lab`}
                  >
                    Open in LCM Lab
                  </Button>

                </Box>

                {canManageLCMs && (
                  <Tooltip title="Delete LCM">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLCM(lcm.id);
                      }}
                      aria-label={`Delete LCM ${lcm.prepro_name}`}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {lcms.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <FunctionsIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No LCMs found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first Load Case Matrix to get started.
          </Typography>
          {canManageLCMs && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateLCMOpen(true)}
            >
              Create Your First LCM
            </Button>
          )}
        </Box>
      )}

      {/* Create LCM Dialog */}
      <Dialog
        open={createLCMOpen}
        onClose={() => { setCreateLCMOpen(false); fieldErrors.clear(); }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-lcm-title"
      >
        <DialogTitle id="create-lcm-title">Create New Load Case Matrix</DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); handleCreateLCM(); }}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="LCM Name"
              fullWidth
              required
              value={lcmForm.prepro_name}
              error={fieldErrors.invalid('prepro_name')}
              onChange={(e) => {
                fieldErrors.clearField('prepro_name');
                setLcmForm({ ...lcmForm, prepro_name: e.target.value });
              }}
              aria-describedby="lcm-name-helper"
            />
            <Typography id="lcm-name-helper" variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              Enter a descriptive name for the Load Case Matrix
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setCreateLCMOpen(false); fieldErrors.clear(); }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
            >
              Create LCM
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

    </Box>
  );
};

export default LCMsPage;